<template>
  <div class="card">
    <h3 class="card-title">
      Cargar empleados masivamente - {{ fechaFormateada }}
    </h3>
    <p class="text-secondary mb-3">
      Seleccione el estado de cada empleado. Para los ausentes complete los
      detalles de licencia.
    </p>

    <div class="d-flex space-between w-100 mb-3">
      <button
        @click="volver"
        class="btn btn-outline-dark"
        style="max-width: auto !important; width: fit-content"
      >
        ← Volver
      </button>
      <div class="text-secondary">
        <small>
          Presentes: {{ cantidadPresentes }} - Ausentes:
          {{ cantidadAusentes }} - Sin definir: {{ cantidadSinDefinir }}
        </small>
      </div>
    </div>

    <div class="empleados-container" v-if="personasSinCargar">
      <div
        v-for="persona in personasSinCargar"
        :key="persona.id"
        class="empleado-card"
        :class="{
          presente: getEmpleadoData(persona.id).estado === 'Presente',
          ausente: getEmpleadoData(persona.id).estado === 'Ausente',
        }"
      >
        <!-- Header del empleado -->
        <div class="empleado-header">
          <div class="empleado-info">
            <strong>{{ persona.nombre }}</strong>
            <small class="text-secondary d-block" v-if="persona.area">{{
              persona.area.descripcion
            }}</small>
          </div>
          <div class="empleado-estado">
            <pd-input
              v-model="getEmpleadoData(persona.id).estado"
              select
              label="Estado"
              placeholder="Seleccione estado"
              @change="estadoChanged(persona)"
            >
              <template slot="options">
                <option value="">Seleccione</option>
                <option value="Presente">Presente</option>
                <option value="Ausente">Ausente</option>
              </template>
            </pd-input>
          </div>
        </div>

        <!-- Contenido expandible según estado -->
        <div class="empleado-content" v-if="getEmpleadoData(persona.id).estado">
          <!-- Si está Presente -->
          <template v-if="getEmpleadoData(persona.id).estado === 'Presente'">
            <pd-input
              v-model="getEmpleadoData(persona.id).notas"
              label="Notas"
              placeholder="Notas opcionales"
            />
          </template>

          <!-- Si está Ausente -->
          <template v-if="getEmpleadoData(persona.id).estado === 'Ausente'">
            <suggest
              :id="'tipo_licencia_' + persona.id"
              label="Licencia"
              placeholder="Buscar licencia..."
              class="my-1"
              :data="tiposLicencia"
              :items="tipoLicenciaList"
              @selected="(licencia) => tipoLicenciaSelected(persona, licencia)"
              :required="!getEmpleadoData(persona.id).licencia_id"
            ></suggest>

            <div class="cols2">
              <pd-input
                v-model="getEmpleadoData(persona.id).licencia_desde"
                :id="'desde_' + persona.id"
                label="Fecha inicio licencia"
                placeholder="Fecha de inicio"
                type="date"
                :required="!getEmpleadoData(persona.id).licencia_desde"
                :class="{ 'input-error': getErrorFechas(persona.id) }"
                @input="validarFechas(persona)"
              />
              <pd-input
                v-model="getEmpleadoData(persona.id).licencia_hasta"
                :id="'hasta_' + persona.id"
                label="Fecha fin licencia"
                placeholder="Fecha de fin"
                type="date"
                :required="!getEmpleadoData(persona.id).licencia_hasta"
                :min="getEmpleadoData(persona.id).licencia_desde"
                :class="{ 'input-error': getErrorFechas(persona.id) }"
                @input="validarFechas(persona)"
              />
            </div>
            <div v-if="getErrorFechas(persona.id)" class="error-fechas">
              <small class="text-danger"
                >La fecha de fin debe ser mayor o igual a la fecha de
                inicio</small
              >
            </div>
          </template>
        </div>
      </div>
    </div>

    <!-- Mensaje cuando no hay empleados para cargar -->
    <div
      v-if="personasSinCargar && personasSinCargar.length === 0"
      class="sin-empleados"
    >
      <div class="text-center">
        <h4 class="text-secondary">No hay empleados pendientes de cargar</h4>
        <p class="text-secondary">
          Todos los empleados ya tienen su parte diario cargado para este día.
        </p>
        <button @click="volver" class="btn btn-primary mt-3">
          Volver al parte diario
        </button>
      </div>
    </div>

    <div
      class="acciones-footer"
      v-if="personasSinCargar && personasSinCargar.length > 0"
    >
      <div class="text-info">
        <small v-if="empleadosListosParaGuardar > 0">
          {{ empleadosListosParaGuardar }} empleados listos para guardar
        </small>
      </div>
      <div class="botones-accion">
        <button @click="volver" class="btn btn-outline-dark">Cancelar</button>
        <button
          @click="guardarTodos"
          class="btn btn-primary ml-2"
          type="button"
          :disabled="!puedeGuardar"
        >
          Guardar partes ({{ empleadosListosParaGuardar }})
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { DateTime } from "luxon";
import { PersonasService } from "../../personas/services/PersonasServices";
import { TiposLicenciasServices } from "../../tipos_licencias/services/TiposLicenciasServices";
import Suggest from "../../../components/elements/Suggest.vue";
import ParteDiarioServices from "../services/ParteDiarioServices";
import Swal from "sweetalert2";

export default {
  name: "CargarParteDiarioMasivo",
  components: {
    Suggest,
  },
  data() {
    return {
      today: null,
      personas: null,
      personasSinCargar: null,
      partesExistentes: null,
      tiposLicencia: null,
      empleadosData: {},
      erroresFechas: {},
      tipoLicenciaList: TiposLicenciasServices.dataList,
    };
  },
  computed: {
    fechaFormateada() {
      return this.today ? this.today.toFormat("dd/LL/yyyy") : "";
    },
    fechaParam() {
      return this.today ? this.today.toFormat("yyyyLLdd") : "";
    },
    cantidadPresentes() {
      return Object.values(this.empleadosData).filter(
        (emp) => emp.estado === "Presente"
      ).length;
    },
    cantidadAusentes() {
      return Object.values(this.empleadosData).filter(
        (emp) => emp.estado === "Ausente"
      ).length;
    },
    cantidadSinDefinir() {
      return Object.values(this.empleadosData).filter((emp) => !emp.estado)
        .length;
    },
    empleadosListosParaGuardar() {
      const empleadosConEstado = Object.values(this.empleadosData).filter(
        (emp) => emp.estado
      );
      const ausentes = empleadosConEstado.filter(
        (emp) => emp.estado === "Ausente"
      );
      const ausentesCompletos = ausentes.filter(
        (emp) => emp.licencia_id && emp.licencia_desde && emp.licencia_hasta
      );
      const presentes = empleadosConEstado.filter(
        (emp) => emp.estado === "Presente"
      );
      return presentes.length + ausentesCompletos.length;
    },
    puedeGuardar() {
      const empleadosConEstado = Object.values(this.empleadosData).filter(
        (emp) => emp.estado
      );
      if (empleadosConEstado.length === 0) return false;

      // Verificar que todos los ausentes tengan licencia configurada
      const ausentes = empleadosConEstado.filter(
        (emp) => emp.estado === "Ausente"
      );
      const todosTienenLicencia = ausentes.every(
        (emp) => emp.licencia_id && emp.licencia_desde && emp.licencia_hasta
      );

      // Verificar que no haya errores de fechas
      const hayErroresFechas = Object.values(this.erroresFechas).some(
        (error) => error === true
      );

      return todosTienenLicencia && !hayErroresFechas;
    },
  },
  async mounted() {
    this.today = DateTime.fromFormat(this.$route.params.date, "yyyyLLdd");

    // Cargar datos necesarios
    this.personas = await PersonasService.api.getForParteDiario();
    this.tiposLicencia = await TiposLicenciasServices.api.getAll();

    // Cargar partes existentes del día
    this.partesExistentes = await ParteDiarioServices.api.findForUser(
      this.today.toFormat("yyyy-LL-dd")
    );

    // Filtrar personas que no están cargadas y ordenar alfabéticamente
    this.personasSinCargar = this.personas
      .filter(
        (persona) =>
          !this.partesExistentes.some(
            (parte) => parte.empleado_id === persona.id
          )
      )
      .sort((a, b) => a.nombre.localeCompare(b.nombre));

    // Inicializar datos de empleados usando $set para reactividad (solo para personas sin cargar)
    this.personasSinCargar.forEach((persona) => {
      this.$set(this.empleadosData, persona.id, {
        empleado_id: persona.id,
        area_id: persona.area_id,
        gerencia_id: persona.gerencia_id,
        fecha: this.today.toFormat("yyyy-LL-dd"),
        estado: "",
        notas: "",
        licencia_id: null,
        licencia_desde: null,
        licencia_hasta: null,
      });
    });
  },
  methods: {
    getEmpleadoData(empleadoId) {
      return this.empleadosData[empleadoId] || {};
    },
    estadoChanged(persona) {
      const data = this.empleadosData[persona.id];

      if (data.estado === "Presente") {
        // Limpiar datos de licencia si cambia a presente
        this.$set(data, "licencia_id", null);
        this.$set(data, "licencia_desde", null);
        this.$set(data, "licencia_hasta", null);
        // Limpiar errores de fechas
        this.$set(this.erroresFechas, persona.id, false);
      } else if (data.estado === "Ausente") {
        // Limpiar notas si cambia a ausente
        this.$set(data, "notas", "");
      } else if (data.estado === "") {
        // Limpiar todo si vuelve a "Seleccione"
        this.$set(data, "notas", "");
        this.$set(data, "licencia_id", null);
        this.$set(data, "licencia_desde", null);
        this.$set(data, "licencia_hasta", null);
        // Limpiar errores de fechas
        this.$set(this.erroresFechas, persona.id, false);
      }

      // Forzar actualización de la vista
      this.$forceUpdate();
    },
    tipoLicenciaSelected(persona, tipoLicencia) {
      if (tipoLicencia && persona) {
        const data = this.empleadosData[persona.id];
        this.$set(data, "licencia_id", tipoLicencia.id);
      }
    },
    getErrorFechas(empleadoId) {
      return this.erroresFechas[empleadoId] || false;
    },
    validarFechas(persona) {
      const data = this.empleadosData[persona.id];

      if (data.licencia_desde && data.licencia_hasta) {
        const fechaDesde = new Date(data.licencia_desde);
        const fechaHasta = new Date(data.licencia_hasta);

        if (fechaHasta < fechaDesde) {
          this.$set(this.erroresFechas, persona.id, true);
        } else {
          this.$set(this.erroresFechas, persona.id, false);
        }
      } else {
        this.$set(this.erroresFechas, persona.id, false);
      }
    },
    volver() {
      this.$router.push({
        name: "CreateAndEditParteDiario",
        params: { date: this.fechaParam },
      });
    },
    async guardarTodos() {
      const empleadosParaGuardar = Object.values(this.empleadosData).filter(
        (emp) => emp.estado
      );

      if (empleadosParaGuardar.length === 0) {
        Swal.fire("Error", "Debe seleccionar al menos un empleado", "error");
        return;
      }

      // Mostrar confirmación
      const confirmResult = await Swal.fire({
        title: "Confirmar guardado",
        text: `¿Está seguro de guardar ${empleadosParaGuardar.length} empleados?`,
        icon: "question",
        showCancelButton: true,
        confirmButtonText: "Sí, guardar",
        cancelButtonText: "Cancelar",
      });

      if (!confirmResult.isConfirmed) return;

      // Mostrar loading
      Swal.fire({
        title: "Guardando...",
        text: "Por favor espere mientras se guardan los partes",
        allowOutsideClick: false,
        allowEscapeKey: false,
        showConfirmButton: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });

      try {
        // Guardar uno por uno usando el servicio existente
        const resultados = [];
        for (const empleado of empleadosParaGuardar) {
          const resultado = await ParteDiarioServices.api.addEmpleadoToParte(
            empleado
          );
          resultados.push(resultado);
        }

        // Verificar si todos se guardaron correctamente
        const exitosos = resultados.filter((r) => r.success === true);
        const fallidos = resultados.filter((r) => r.success !== true);

        if (fallidos.length === 0) {
          Swal.fire(
            "Éxito",
            `Se guardaron exitosamente ${exitosos.length} empleados`,
            "success"
          );
          // Redirigir de vuelta a la vista principal
          this.$router.push({
            name: "CreateAndEditParteDiario",
            params: { date: this.fechaParam },
          });
        } else {
          Swal.fire(
            "Guardado parcial",
            `Se guardaron ${exitosos.length} empleados exitosamente. ${fallidos.length} fallaron.`,
            "warning"
          );
        }
      } catch (error) {
        console.error("Error al guardar:", error);
        Swal.fire("Error", "Ocurrió un error al guardar los partes", "error");
      }
    },
  },
};
</script>

<style scoped>
.empleados-container {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  max-height: 600px;
  overflow-y: auto;
  padding: 0.5rem;
}

.empleado-card {
  border: 1px solid #dee2e6;
  border-radius: 0.5rem;
  padding: 1rem;
  background-color: white;
  transition: all 0.2s ease;
}

.empleado-card:hover {
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.empleado-card.presente {
  border-left: 4px solid #28a745;
  background-color: #f8fff9;
}

.empleado-card.ausente {
  border-left: 4px solid #dc3545;
  background-color: #fff8f8;
}

.empleado-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 1rem;
  gap: 1rem;
}

.empleado-info {
  flex: 1;
}

.empleado-info strong {
  font-size: 1.1rem;
  color: #333;
}

.empleado-estado {
  min-width: 200px;
}

.empleado-content {
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid #eee;
  animation: slideDown 0.3s ease-out;
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.acciones-footer {
  margin-top: 2rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  border-top: 1px solid #dee2e6;
  background-color: #f8f9fa;
  border-radius: 0 0 0.5rem 0.5rem;
}

.botones-accion {
  display: flex;
  gap: 0.5rem;
}

.text-info {
  color: #17a2b8 !important;
  font-weight: 500;
}

.sin-empleados {
  padding: 3rem 2rem;
  text-align: center;
}

.text-center {
  text-align: center;
}

.mt-3 {
  margin-top: 1rem !important;
}

.error-fechas {
  margin-top: 0.5rem;
}

.text-danger {
  color: #dc3545 !important;
}

.input-error >>> input {
  border-color: #dc3545 !important;
  box-shadow: 0 0 0 0.2rem rgba(220, 53, 69, 0.25) !important;
}

.input-error >>> .form-control {
  border-color: #dc3545 !important;
  box-shadow: 0 0 0 0.2rem rgba(220, 53, 69, 0.25) !important;
}

.text-secondary {
  color: #6c757d !important;
}

.d-block {
  display: block !important;
}

.d-flex {
  display: flex !important;
}

.justify-content-between {
  justify-content: space-between !important;
}

.space-between {
  justify-content: space-between !important;
}

.w-100 {
  width: 100% !important;
}

.mt-4 {
  margin-top: 1.5rem !important;
}

.mb-3 {
  margin-bottom: 1rem !important;
}

.mr-2 {
  margin-right: 0.5rem !important;
}

.ml-2 {
  margin-left: 0.5rem !important;
}

.my-1 {
  margin-top: 0.25rem !important;
  margin-bottom: 0.25rem !important;
}

@media (max-width: 768px) {
  .empleado-header {
    flex-direction: column;
    align-items: stretch;
  }

  .empleado-estado {
    min-width: auto;
  }

  .empleados-container {
    max-height: 400px;
  }
}
</style>
